import * as React$1 from "react";
import { useContext } from "react";
//#region src/AuthContext.ts
var DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE = "Sorry, something went wrong.";
var AuthContext = React$1.createContext({
	authentication: {
		state: "not_authenticated",
		user: null
	},
	logout: () => {},
	refresh: () => {},
	verifyLogin: async () => ({
		success: false,
		displayErrorMessage: DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE
	}),
	validateResetPasswordToken: async () => ({ success: true }),
	resetPassword: async () => ({ success: true })
});
/** Gets AuthenticationState. */
function useAuth() {
	const authContext = useContext(AuthContext);
	return {
		...authContext,
		user: authContext.authentication.user
	};
}
/**
* Gets AuthenticationState, but only if used in an authenticated context.
*
* This hook will guarantee that a user exists, but throws an error if not in an authenticated context.
*
* ```typescript
* const Hello = () => {
*   const { authentication } = useAuth();
*   if (authentication.state === 'authenticated') {
*     return <HelloAuthenticated />;
*   } else {
*     return <HelloMaybeAuthenticated />;
*   }
* };
*
* const HelloAuthenticated = () => {
*   // GOOD. This will get the user and we don't have to check for null/undefined.
*   const { user } = useAuthAuthenticated();
*   return `Hello, ${user.name}`;
* };
*
* const HelloMaybeAuthenticated = () => {
*   // BAD. This will crash.
*   const { user } = useAuthAuthenticated();
*
*   // GOOD. This will get the user but we must check for null/undefined before using it.
*   const { user } = useAuth();
*   if (user) {
*     return `Hello, ${user.name}`;
*   } else {
*     return 'Hello, stranger';
*   }
* };
* ```
* @throws an error if not in an authenticated context
*/
function useAuthAuthenticated() {
	const { authentication, logout, refresh, verifyLogin, validateResetPasswordToken, resetPassword } = useAuth();
	if (authentication.state === "authenticated") return {
		authentication,
		user: authentication.user,
		logout,
		refresh,
		verifyLogin,
		validateResetPasswordToken,
		resetPassword
	};
	else throw new Error("DEVELOPER ERROR: useAuthAuthenticated must be used in an authenticated context");
}
//#endregion
export { AuthContext, DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE, useAuth, useAuthAuthenticated };

